import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import IcFilterFunnel from "@/assets/icons/ic-filter-funnel-01.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/common";
import { getPublicAgents, type SortOption } from "@/services/agent-list-service";
import type { AgentListItem } from "@/types/agent";
import { AgentCard } from "./components/agent-card";
import IcXRemove from "@/assets/icons/ic-x-remove.svg";
import IcSearchMd from "@/assets/icons/ic-search-md.svg";
import IlNoData from "@/assets/icons/il-no-data.svg";
import { LoaderIcon } from "lucide-react";

const ITEMS_PER_PAGE = 9;

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "newest", label: "최신순" },
  { value: "oldest", label: "오래된순" },
  { value: "most_used", label: "인기순" },
  { value: "name", label: "이름순" },
];

export default function AgentListPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [sort, setSort] = useState<SortOption>("newest");

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const fetchAgents = useCallback(async (page: number, search: string, sortBy: SortOption) => {
    setIsLoading(true);
    if (page === 1) {
      setAgents([]);
      setTotal(0);
    }
    try {
      const result = await getPublicAgents({
        page,
        limit: ITEMS_PER_PAGE,
        search,
        sort: sortBy,
      });
      setAgents(result.items);
      setTotal(result.total);
    } catch {
      setAgents([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents(currentPage, activeSearch, sort);
  }, [currentPage, activeSearch, sort, fetchAgents]);

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearch(searchInput.trim());
      setCurrentPage(1);
    }
  };

  const handleSortChange = (value: SortOption) => {
    setSort(value);
    setCurrentPage(1);
  };



  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="flex flex-col items-center gap-6 px-5 py-6  lg:py-10 max-w-[1120px] px-5 min-[1120px]:px-0 mx-auto w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 w-full">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-bold leading-[24px] lg:leading-[28px] tracking-[-0.018em] lg:tracking-[-0.02em] text-[#202020]">
              AI 에이전트 및 솔루션
            </h1>
            <p className="text-sm leading-[20px] tracking-[-0.01em] text-[#000000]">
              실제 비즈니스 목적을 달성하게 해주는 신뢰할 수 있는 AI 에이전트와 솔루션을 만나보세요.
            </p>
          </div>
        </div>

        {/* Search + Sort */}
        <div className="flex items-center gap-3 lg:gap-6 w-full">
          <div className="flex-1 min-w-0 lg:w-[400px] lg:flex-none">
            <div className="relative flex items-center gap-2 h-10 lg:h-12 px-4 rounded-[4px] border border-[rgba(0,0,0,0.15)] transition-colors focus-within:border-black">
              <img src={IcSearchMd} alt="" className="w-5 h-5 lg:w-6 lg:h-6 shrink-0" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onBlur={(e) => setSearchInput(e.target.value.trim())}
                onKeyDown={handleSearch}
                placeholder="AI 에이전트 및 솔루션 검색..."
                className="peer flex-1 min-w-0 bg-transparent text-base text-[#000000] placeholder:text-[#B0B0B0] outline-none"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSearchInput("");
                    setActiveSearch("");
                    setCurrentPage(1);
                  }}
                  className="shrink-0 w-6 h-6 p-0"
                  aria-label="검색어 지우기"
                >
                  <img src={IcXRemove} alt="" className="w-5 h-5" />
                </Button>
              )}
              {/* <span className="absolute bottom-0 left-4 right-4 h-[1.5px] bg-[#171717] opacity-0 peer-focus:opacity-100 transition-opacity" /> */}
            </div>
          </div>

          <div className="hidden lg:flex flex-1 justify-end" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={"ghost"}
                className="p-2 g:w-auto lg:h-12 lg:px-5 lg:pl-4 rounded-[4px] border border-[rgba(0,0,0,0.15)] bg-white text-base font-bold text-[#000000] hover:bg-black/[0.04] transition-colors !outline-none !focus-visible:outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0"
              >
                <div className="relative">
                  <img src={IcFilterFunnel} alt="" className="w-6 h-6" />
                  {sort !== "newest" && (
                    <span className="absolute -top-0.5 -right-0.5 lg:hidden w-2 h-2 bg-[#FFAA72] rounded-[3px] border-[1.3px] border-white" />
                  )}
                </div>
                <span className="hidden lg:inline">
                  {SORT_OPTIONS.find((o) => o.value === sort)?.label}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              {SORT_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleSortChange(option.value)}
                  className={sort === option.value ? "font-bold" : ""}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Divider - desktop only */}
        <div className=" w-full h-px bg-[rgba(0,0,0,0.15)]" />

        {/* Search result count */}
        {activeSearch && (
          <p className="w-full text-base font-semibold text-[#000000]">검색 결과 {total}건</p>
        )}

        {/* Agent list or empty state */}
        {agents.length === 0 ? (
          <div className="flex flex-col items-center lg:gap-[12px] gap-2 py-20 w-full">
            <img src={IlNoData} alt="" />
            <div className="flex flex-col items-center gap-2">
              <span className="text-[24px] lg:text-[32px] leading-[28px] lg:leading-10 tracking-[-0.03em] text-black">
                No data
              </span>
              <p className="text-base font-normal leading-6 tracking-[-0.01em] text-[#000000] text-center mt-2">
                {activeSearch ? "" : "아직 생성된 에이전트가 없습니다"}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
              {agents.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  onClick={(id) => navigate(`/agents/${id}`)}
                />
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </>
  );
}
